// src/hooks/klines/adapters/bitgetFutures.js
const PROXY  = `${process.env.REACT_APP_BACKEND_URL}/proxy`;
const REST   = "https://api.bitget.com";
const WS_URL = "wss://ws.bitget.com/v2/ws/public";

/* ---------- helpers ---------- */

const TF_MS = {
  "1m":60_000, "3m":180_000, "5m":300_000, "15m":900_000, "30m":1_800_000,
  "1h":3_600_000, "2h":7_200_000, "4h":14_400_000, "6h":21_600_000, "8h":28_800_000, "12h":43_200_000,
  "1d":86_400_000, "3d":259_200_000, "1w":604_800_000, "1M":2_592_000_000,
};

const asNum = (x) => Number.parseFloat(x);
const toSec = (msOrSec) => {
  const n = Number(msOrSec);
  if (!Number.isFinite(n)) return undefined;
  return n > 2e10 ? Math.floor(n / 1000) : Math.floor(n);
};
const toLwc = (t, o, h, l, c) => ({ time: t, open:+o, high:+h, low:+l, close:+c });

/** Bitget v2 granularity (ровно как в доке) */
function mapIntervalToV2(interval) {
  return ({
    "1m":"1m","3m":"3m","5m":"5m","15m":"15m","30m":"30m",
    "1h":"1H","4h":"4H","6h":"6H","12h":"12H",
    "1d":"1D","3d":"3D","1w":"1W","1M":"1M",
  }[interval] || "1m");
}

/** Парсим символ Bitget mix:
 *  - OPENUSDT_UMCBL  -> { sym:"OPENUSDT", productType:"USDT-FUTURES" }
 *  - BTCUSD_DMCBL    -> { sym:"BTCUSD",  productType:"COIN-FUTURES" }
 *  - ETHUSDC_USDCBL  -> { sym:"ETHUSDC", productType:"USDC-FUTURES" }
 *  - Если без суффикса — считаем USDT-FUTURES по умолчанию
 */
function parseBitgetMixSymbol(raw) {
  const s = String(raw || "");
  if (/_UMCBL$/i.test(s))  return { sym: s.replace(/_UMCBL$/i, ""), productType: "USDT-FUTURES" };
  if (/_DMCBL$/i.test(s))  return { sym: s.replace(/_DMCBL$/i, ""), productType: "COIN-FUTURES" };
  if (/_USDCBL$/i.test(s)) return { sym: s.replace(/_USDCBL$/i, ""), productType: "USDC-FUTURES" };
  // fallback — иногда приходит уже без суффикса:
  return { sym: s, productType: "USDT-FUTURES" };
}

/** Округление времени к началу «корзины», как просит Bitget для start/end */
function snapToBucketMs(tsMs, tfMs) {
  const n = Number(tsMs);
  if (!Number.isFinite(n)) return undefined;
  return Math.floor(n / tfMs) * tfMs;
}

/** Нормализация ответа v2:
 * data: [ [ "ts","o","h","l","c","baseVol","quoteVol" ], ... ]
 */
function normalizeV2(body, limit) {
  const rows = Array.isArray(body?.data) ? body.data : (Array.isArray(body) ? body : []);
  const out = rows.map(a => {
    const ts = toSec(a?.[0]);
    const o = +a?.[1], h = +a?.[2], l = +a?.[3], c = +a?.[4];
    if ([ts,o,h,l,c].every(Number.isFinite)) return toLwc(ts, o,h,l,c);
    return null;
  }).filter(Boolean)
    .sort((x,y) => x.time - y.time)
    .slice(-Math.max(1, limit || 300));
  return out;
}

/* ---------- REST: история свечей (Bitget v2 /api/v2/mix/market/candles) ---------- */
/**
 * @param {Object} p
 * @param {string} p.symbol    Например "OPENUSDT_UMCBL" или "BTCUSDT"
 * @param {string} [p.interval="1m"]
 * @param {number} [p.limit=300]  // max 1000
 * @param {number} [p.startTime]  // ms; опционально
 * @param {number} [p.endTime]    // ms; опционально
 */
export async function bitgetFuturesFetchHistory({
  symbol,
  interval = "1m",
  limit = 300,
  startTime,
  endTime,
}) {
  if (!symbol) return [];

  const { sym, productType } = parseBitgetMixSymbol(symbol);
  const granularity = mapIntervalToV2(interval);
  const tfMs = TF_MS[interval] || TF_MS["1m"];

  // Bitget v2 хочет ms-темплейты, округлённые по frame (из доки)
  const nowMs = Date.now();
  const endMsRaw   = Number.isFinite(endTime)   ? +endTime   : nowMs;
  const startMsRaw = Number.isFinite(startTime) ? +startTime : (endMsRaw - tfMs * Math.max(1, limit));

  const endMs   = snapToBucketMs(endMsRaw, tfMs);
  const startMs = snapToBucketMs(startMsRaw, tfMs);

  // v2: productType обязателен; startTime/endTime опциональны (макс диапазон 90 дней)
  const qs = new URLSearchParams();
  qs.set("symbol", sym);
  qs.set("productType", productType);
  qs.set("granularity", granularity);
  qs.set("limit", String(Math.min(1000, Math.max(1, limit)))); // по умолчанию 100
  // передаём start/end только если они валидны
  if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs) {
    qs.set("startTime", String(startMs));
    qs.set("endTime",   String(endMs));
  }

  const url = `${PROXY}/${REST}/api/v2/mix/market/candles?${qs.toString()}`;
  console.log("[bitgetFut REST v2] fetch", { url, sym, productType, granularity, limit });

  const r = await fetch(url, { headers: { 'ngrok-skip-browser-warning': '1' } });
  if (!r.ok) {
    // попробуем без start/end (некоторые кластеры на гранилях капризничают)
    const qs2 = new URLSearchParams();
    qs2.set("symbol", sym);
    qs2.set("productType", productType);
    qs2.set("granularity", granularity);
    qs2.set("limit", String(Math.min(1000, Math.max(1, limit))));
    const url2 = `${PROXY}/${REST}/api/v2/mix/market/candles?${qs2.toString()}`;
    console.warn("[bitgetFut REST v2] retry without start/end", { url2 });
    const r2 = await fetch(url2, { headers: { 'ngrok-skip-browser-warning': '1' } });
    if (!r2.ok) throw new Error(`Bitget FUT HTTP ${r2.status}`);
    const body2 = await r2.json().catch(()=>([]));
    const out2 = normalizeV2(body2, limit);
    console.log("[bitgetFut REST v2] ok(retry)", { got: out2.length, first: out2[0], last: out2[out2.length-1] });
    return out2;
  }

  const body = await r.json().catch(()=>([]));
  const out = normalizeV2(body, limit);
  console.log("[bitgetFut REST v2] ok", { got: out.length, first: out[0], last: out[out.length-1] });
  return out;
}

/* ---------- WS: свечи (v2 публичный сокет) ---------- */
function mapIntervalToWs(interval) {
  return ({
    "1m":"candle1m","3m":"candle3m","5m":"candle5m","15m":"candle15m","30m":"candle30m",
    "1h":"candle1H","4h":"candle4H","6h":"candle6H","12h":"candle12H",
    "1d":"candle1D","3d":"candle3D","1w":"candle1W","1M":"candle1M",
  }[interval] || "candle1m");
}

export function bitgetFuturesOpenWS({ symbol, interval = "1m", onBar }) {
  const chan = mapIntervalToWs(interval);
  const { sym, productType } = parseBitgetMixSymbol(symbol);
  console.log("[bitgetFut WS] open", { sym, productType, interval, chan });

  const ws = new WebSocket(WS_URL);
  let subscribed = false;

  const close = () => {
    try {
      if (subscribed && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({
          op: "unsubscribe",
          args: [{ instType: productType, channel: chan, instId: sym }]
        }));
        console.log("[bitgetFut WS] unsubscribe", { sym, chan });
      }
    } catch {}
    try { ws.close(); } catch {}
    console.log("[bitgetFut WS] closed");
  };

  ws.onopen = () => {
    const sub = {
      op: "subscribe",
      args: [{ instType: productType, channel: chan, instId: sym }]
    };
    try { ws.send(JSON.stringify(sub)); } catch {}
    subscribed = true;
    console.log("[bitgetFut WS] subscribed", sub);
  };

  ws.onmessage = (evt) => {
    try {
      const msg = JSON.parse(evt.data);
      if (!msg) return;

      if (msg.event === "subscribe") {
        console.log("[bitgetFut WS] ack", msg.arg);
        return;
      }
      if (msg.event === "error") {
        console.warn("[bitgetFut WS] error", msg);
        return;
      }
      if (!msg?.data || !Array.isArray(msg.data)) return;

      // data: [ [ "ts","o","h","l","c","baseVol","quoteVol" ] ]
      for (const row of msg.data) {
        const ts = toSec(row?.[0]);
        const o = +row?.[1], h = +row?.[2], l = +row?.[3], c = +row?.[4];
        if ([ts,o,h,l,c].every(Number.isFinite)) onBar?.(toLwc(ts, o,h,l,c));
      }
    } catch (e) {
      console.warn("[bitgetFut WS] parse error", e);
    }
  };

  ws.onerror = (e) => console.warn("[bitgetFut WS] error", e);
  ws.onclose  = () => console.log("[bitgetFut WS] onclose");

  return close;
}

export default {
  bitgetFuturesFetchHistory,
  bitgetFuturesOpenWS,
};
